import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./Services.css";
import "./AddingServices.css";
import axios from "axios";
import url from "../../../Utility/URL/url";
import { toast } from "react-toastify";
import constant from "../../../Utility/Constants/constant";
import { getPriceUtility } from "../../../Utility/Price/getPriceUtility";
import authHeader from "../../../headerTokenUtility/auth.header";
import getUsernameFromSession from "../../../Utility/gettingInformation/getUsernameFromSession";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

function Services() {
  const navigate = useNavigate();
  const [skeleton, setSkeleton] = useState(true);
  const [servicesList, setServicesList] = useState([]);
  const [selectedFilterOption, setSelectedFilterOption] = useState("All");
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [copied, setCopied] = useState(false);
  const shareRef = useRef(null);

  // menu related
  const [openMenuFor, setOpenMenuFor] = useState(null); // platformServiceId of open menu
  const [shareModalFor, setShareModalFor] = useState(null); // service object to show share modal
  const menuRef = useRef(null);

  const username = getUsernameFromSession();
  const currency = "Rs.";
  const filterOptions = ["All", "1:1 Call", "Text Query"];

  // ✅ Fetch all services initially
  const fetchServices = async () => {
    try {
      setSkeleton(true);
      const res = await axios.get(
        url.GET_ALL_SERVICE + `?username=${username}`,
        {
          headers: authHeader(),
        }
      );

      // Sort services by displayPriority for proper initial order
      const sorted = (res.data || []).sort((a, b) => {
        const aVal = (a.displayPriority || "").replace("_", ".");
        const bVal = (b.displayPriority || "").replace("_", ".");
        return parseFloat(aVal || 0) - parseFloat(bVal || 0);
      });

      setServicesList(
        sorted.map((s) => ({ ...s, disableService: !!s.disableService }))
      );
    } catch (err) {
      toast.error("Failed to fetch services!");
      console.error(err.response?.data || err);
    } finally {
      setSkeleton(false);
    }
  };

  useEffect(() => {
    if (username) fetchServices();
  }, [username]);

  // ✅ Filter services
  useEffect(() => {
    if (selectedFilterOption === "All") {
      fetchServices();
      return;
    }

    const filterType =
      selectedFilterOption === "1:1 Call"
        ? constant.ONE_ON_ONE_CALL
        : constant.TEXT_QUERY;

    axios
      .post(
        url.GET_SERVICE_BY_USERNAME_AND_TYPE,
        { username, type: filterType },
        { headers: authHeader() }
      )
      .then((res) => setServicesList(res.data))
      .catch(() => toast.error("Network Error!"));
  }, [selectedFilterOption]);

  // ✅ Delete a service
  const handleDelete = async (serviceId) => {
    try {
      await axios.delete(url.DELETE_SERVICE_URL + `/${serviceId}`, {
        headers: authHeader(),
      });
      toast.success("Service deleted successfully!");
      fetchServices();
    } catch (err) {
      toast.error("Failed to delete service!");
      console.error(err.response?.data || err);
    }
  };

  // ✅ Toggle visibility
  const handleToggleIcon = async (service, index) => {
    const newValue = !service.hideInProfileFlag;

    setServicesList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], hideInProfileFlag: newValue };
      return updated;
    });

    try {
      await axios.post(
        url.POST_TOGGLE_VALUE,
        {},
        {
          headers: authHeader(),
          params: {
            platformServiceId: service.platformServiceId,
            value: newValue ? "on" : "off",
          },
        }
      );
      toast.success(newValue ? "Service hidden!" : "Service visible!");
    } catch (err) {
      setServicesList((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], hideInProfileFlag: !newValue };
        return updated;
      });
      toast.error("Failed to update visibility!");
      console.error(err.response?.data || err);
    }
  };

  // ✅ Save all priorities to API in one request
  const savePrioritiesToAPI = async (updatedServices) => {
    try {
      const payload = {
        username,
        services: updatedServices.map((s) => ({
          platformServiceId: s.platformServiceId,
          displayPriority: s.displayPriority, // "1", "1_1", "2", "2_2"
        })),
      };

      await axios.post(url.UPDATE_SERVICE_PRIORITY, payload, {
        headers: authHeader(),
      });
      toast.success("Service priorities updated!");
    } catch (err) {
      toast.error("Failed to save priorities!");
      console.error(err.response?.data || err);
    }
  };

  // ✅ Drag-and-drop handler
  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    // Make a copy of the current services list
    const items = Array.from(servicesList);
    const [dragged] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, dragged);

    // Assign displayPriority for 2-column layout
    const updatedServices = items.map((service, index) => {
      const row = Math.floor(index / 2) + 1;
      const col = index % 2 === 0 ? "" : `_${row}`;
      return {
        ...service,
        displayPriority: col === "" ? `${row}` : `${row}${col}`, // "1", "1_1", "2", "2_2"
      };
    });

    // Update UI immediately
    setServicesList(updatedServices);

    // Save updated priorities to backend per service
    try {
      await Promise.all(
        updatedServices.map((service) =>
          axios.put(
             url.PUT_SERVICE_BY_SERVICE_ID + `${service.platformServiceId}`,
            {
              platformServiceId: service.platformServiceId,
              username: username,
              title: service.title,
              shortDescription: service.shortDescription || "",
              additionalQuestion: service.additionalQuestion || "",
              price: service.price,
              type: service.type,
              availability: service.availability || "default",
              description: service.description || "",
              displayPriority: service.displayPriority,
              callDuration: service.callDuration || "",
              hideInProfileFlag: service.hideInProfileFlag ? 1 : 0,
               disableService: service.disableService ? 1 : 0,
            },
            { headers: authHeader() }
          )
        )
      );
      toast.success("Service priorities updated successfully!");
    } catch (err) {
      toast.error("Failed to save priorities to API!");
      console.error(err.response?.data || err);
      // on failure, re-fetch to restore authoritative server state
      fetchServices();
    }
  };

  // const getReadableServiceType = (type) => {
  //   if (type === constant.ONE_ON_ONE_CALL) return "ONE ON ONE CALL";
  //   if (type === constant.TEXT_QUERY) return "TEXT QUERY";
  //   return type;
  // };

  // three-dot menu helpers
  useEffect(() => {
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuFor(null);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const openMenu = (platformServiceId) => {
    setOpenMenuFor((prev) =>
      prev === platformServiceId ? null : platformServiceId
    );
  };

  // SHARE: show modal with link and allow copy
  const handleShare = (service) => {
    const domain = window.location.origin;
    const username = getUsernameFromSession(); // already imported in your file

    let shareLink = "";

    if (service.type === constant.TEXT_QUERY) {
      // TEXT QUERY → add priority + dm
      shareLink = `${window.location.origin}/${username}/${service.platformServiceId}/priority/dm`;
    } else if (service.type === constant.ONE_ON_ONE_CALL) {
      // 1:1 CALL → simple link
      shareLink = `${window.location.origin}/${username}/${service.platformServiceId}`;
    }

    setShareModalFor({ service, link: shareLink });
    setOpenMenuFor(null);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);

      setCopied(true); // change button to "Copied"

      setTimeout(() => {
        setCopied(false); // reset after 2 sec
      }, 2000);
    } catch {
      console.error("Copy failed!");
    }
  };
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (shareRef.current && !shareRef.current.contains(e.target)) {
        setShareModalFor(null);
      }
    };

    if (shareModalFor) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [shareModalFor]);

  // DISABLE: toggle disableService in UI and persist via PUT
  // const toggleDisable = async (service) => {
  //   const id = service.platformServiceId;
  //   const newDisabled = !service.disableService;
  //   // optimistic UI update
  //   setServicesList((prev) =>
  //     prev.map((s) =>
  //       s.platformServiceId === id ? { ...s, disableService: newDisabled } : s
  //     )
  //   );
  //   setOpenMenuFor(null);
  //   try {
  //     await axios.put(
  //       `https://api.advisingbuddy.com/advisor/dashboard/service/update/${id}`,
  //       {
  //         platformServiceId: id,
  //         username,
  //         title: service.title,
  //         shortDescription: service.shortDescription || "",
  //         additionalQuestion: service.additionalQuestion || "",
  //         price: service.price,
  //         type: service.type,
  //         availability: service.availability || "default",
  //         description: service.description || "",
  //         displayPriority: service.displayPriority,
  //         callDuration: service.callDuration || "",
  //         hideInProfileFlag: service.hideInProfileFlag ? 1 : 0,
  //         disableService: newDisabled ? 1 : 0,
  //       },
  //       { headers: authHeader() }
  //     );
  //     toast.success(newDisabled ? "Service disabled" : "Service enabled");
  //   } catch (err) {
  //     toast.error("Failed to change disable state!");
  //     console.error(err.response?.data || err);
  //     // rollback
  //     setServicesList((prev) =>
  //       prev.map((s) =>
  //         s.platformServiceId === id
  //           ? { ...s, disableService: service.disableService }
  //           : s
  //       )
  //     );
  //   }
  // }; my working code of 27 nov 

  // Reorder after disabling a service
// Reorder list after disable/enable and assign unique priorities to disabled cards too
const reorderAfterDisable = (list) => {
  // Separate enabled and disabled services
  const enabled = list.filter(s => !s.disableService);
  const disabled = list.filter(s => s.disableService);

  // Recalculate displayPriority for enabled services
  const updatedEnabled = enabled.map((service, index) => {
    const row = Math.floor(index / 2) + 1;
    const col = index % 2 === 0 ? "" : `_${row}`;
    return {
      ...service,
      displayPriority: col === "" ? `${row}` : `${row}${col}`,
    };
  });

  // Recalculate displayPriority for disabled services, continue numbering after enabled
  const updatedDisabled = disabled.map((service, index) => {
    const totalEnabled = updatedEnabled.length;
    const position = totalEnabled + index;
    const row = Math.floor(position / 2) + 1;
    const col = position % 2 === 0 ? "" : `_${row}`;
    return {
      ...service,
      displayPriority: col === "" ? `${row}` : `${row}${col}`,
    };
  });

  return [...updatedEnabled, ...updatedDisabled];
};

const toggleDisable = async (service) => {
  const id = service.platformServiceId;
  const newDisabled = !service.disableService;

  // 1. Toggle disable in UI
  let updatedList = servicesList.map(s =>
    s.platformServiceId === id ? { ...s, disableService: newDisabled } : s
  );

  // 2. Reorder and assign displayPriority for all cards
  updatedList = reorderAfterDisable(updatedList);

  // 3. Update UI immediately
  setServicesList(updatedList);
  setOpenMenuFor(null);

  try {
    // 4. Save updated list to backend
    await Promise.all(
      updatedList.map(s =>
        axios.put(
           url.PUT_SERVICE_BY_SERVICE_ID + `${s.platformServiceId}`,
          {
            platformServiceId: s.platformServiceId,
            username,
            title: s.title,
            shortDescription: s.shortDescription || "",
            additionalQuestion: s.additionalQuestion || "",
            price: s.price,
            type: s.type,
            availability: s.availability || "default",
            description: s.description || "",
            displayPriority: s.displayPriority, // unique priority for all cards
            callDuration: s.callDuration || "",
            hideInProfileFlag: s.hideInProfileFlag ? 1 : 0,
            disableService: s.disableService ? 1 : 0,
          },
          { headers: authHeader() }
        )
      )
    );

    toast.success(newDisabled ? "Service disabled" : "Service enabled");
  } catch (err) {
    toast.error("Failed to update service!");
    console.error(err.response?.data || err);

    // rollback
    fetchServices();
  }
};



//   const reorderAfterDisable = (list) => {
//   // 1. Separate enabled and disabled services
//   const enabled = list.filter(s => !s.disableService);
//   const disabled = list.filter(s => s.disableService);

//   // 2. Re-assign priorities ONLY for enabled ones
//   const updatedEnabled = enabled.map((service, index) => {
//     const row = Math.floor(index / 2) + 1;
//     const col = index % 2 === 0 ? "" : `_${row}`;
//     return {
//       ...service,
//       displayPriority: col === "" ? `${row}` : `${row}${col}`,
//     };
//   });

//   // 3. Disabled items added at bottom WITHOUT touching their priority
//   return [...updatedEnabled, ...disabled];
// };


  // DUPLICATE: navigate to add page with service as state to prefill the form
  const handleDuplicate = (service) => {
    // setOpenMenuFor(null);
    navigate(`/dashboard/services/add`, { state: { duplicateFrom: service } });
  };

  const getReadableServiceType = (type) => {
    if (type === constant.ONE_ON_ONE_CALL) return "ONE ON ONE CALL";
    if (type === constant.TEXT_QUERY) return "TEXT QUERY";
    return type;
  };

  return (
    <div className="services" style={{ background: "#f8f9fc", height: "100%" }}>
      <div className="header" style={{ background: "white" }}>
        <div className="header-title flex-row">
          <p
            className="text-start fs-3 pb-0 fw-bold"
            style={{ padding: "10px 0" }}
          >
            Services
          </p>
          <p className="header-subtitle pb-2">
            Manage your advising services and pricing
          </p>
        </div>

        {/* Filter + Add Service */}
        <div
          className="header-buttons"
          style={{
            borderTop: "1px solid rgb(229, 231, 235)",
            paddingTop: "25px",
            paddingLeft: "23px",
            background: "#f8fafc",
          }}
        >
          <div className="d-flex justify-content-between pr-5">
            <div
              className="d-flex flex-row flex-wrap justify-content-start"
              style={{ gap: "1rem" }}
            >
              {filterOptions.map((btn) => (
                <button
                  key={btn}
                  className={`btn filter-btn ${btn
                    .toLowerCase()
                    .replace(/[: ]/g, "-")} ${
                    selectedFilterOption === btn ? "active-filter-button" : ""
                  }`}
                  onClick={() => setSelectedFilterOption(btn)}
                >
                  {btn}
                </button>
              ))}
            </div>

            <div className="addServiceButton">
              <Link to="/dashboard/services/add">
                <button className="btn btn-dark">+ Add Services</button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Drag-and-drop services */}
      <div
        className="main"
        style={{ padding: "24px 22px 60px", height: "-webkit-fill-available" }}
      >
        {skeleton ? (
          <p>Loading...</p>
        ) : servicesList.length === 0 ? (
          <div className="row">
            <div className="col-4">
              <div className="first-service d-flex flex-column flex-wrap border rounded w-100 p-3">
                <h2>Add your first service</h2>
                <p>
                  Experts use Advising Buddy for consulting, mentoring, quick
                  calls, and more.
                </p>
                <button
                  className="btn btn-large bg-danger text-white"
                  onClick={() => navigate("/dashboard/services/add")}
                >
                  Add a service
                </button>
              </div>
            </div>
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="servicesList">
              {(provided) => (
                <div
                  className="service-list"
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {servicesList.map((service, index) => (
                    <Draggable
                      key={service.platformServiceId}
                      draggableId={service.platformServiceId.toString()}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          className={`card service-card 
        ${snapshot.isDragging ? "rbd-dragging" : ""}
        ${snapshot.isDraggingOver ? "rbd-hover-card" : ""}`}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          {/* three-dot menu button (always clickable) */}
                          <div
                            className="card-menu"
                            style={{
                              position: "absolute",
                              right: 9,
                              top: 9,
                              zIndex: 5,
                            }}
                          >
                            <button
                              className="menu-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                openMenu(service.platformServiceId);
                              }}
                            >
                              <i className="fa-solid fa-ellipsis-vertical fa-sm"></i>
                            </button>
                            {openMenuFor === service.platformServiceId && (
                              <div
                                className="three-dot-menu"
                                ref={menuRef}
                                style={{
                                  position: "absolute",
                                  right: 0,
                                  top: 30,
                                  background: "#fff",
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                                  borderRadius: 6,
                                  padding: 8,
                                  minWidth: 180,
                                }}
                              >
                                <button
                                  className="menu-item"
                                  onClick={() => handleShare(service)}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                  }}
                                >
                                  <i className="fa-solid fa-share"></i> Share
                                  Service
                                </button>
                                <button
                                  className="menu-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenuFor(null);
                                    handleDuplicate(service);
                                  }}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                  }}
                                >
                                  <i className="fa-solid fa-copy"></i> Duplicate
                                </button>
                                {/* <button className="menu-item" onClick={() => navigate(`/dashboard/services/edit`, { state: { service } })} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <i className="fa-solid fa-pen-to-square"></i> Edit
                                </button> */}
                                <button
                                  className={`menu-item ${
                                    service.disableService ? "green" : "danger"
                                  }`}
                                  onClick={() => toggleDisable(service)}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                  }}
                                >
                                  <i
                                    className={`fa-solid fa-ban ${
                                      service.disableService
                                        ? "green"
                                        : "danger"
                                    }`}
                                  ></i>{" "}
                                  {service.disableService
                                    ? "Enable"
                                    : "Disable"}{" "}
                                  Service
                                </button>
                              </div>
                            )}
                          </div>
                          <div
                            className={`card-body p-0 ${
                              service.disableService ? "disabled" : ""
                            }`}
                            style={{
                              opacity: service.disableService ? 0.45 : 1,
                            }}
                          >
                            <div className="card-content">
                              <div className="d-flex align-items-start justify-content-between">
                                <div>
                                  <h5
                                    className="card-title text-start"
                                    style={{
                                      marginBottom: "6px",
                                      fontSize: "1.35",
                                      fontWeight: "600",
                                      color: "#1a1a1a",
                                    }}
                                  >
                                    {service.title}
                                  </h5>
                                  {/* <span className="priority">#{service.displayPriority}</span> */}
                                  <div
                                    className="description text-left m-0"
                                    dangerouslySetInnerHTML={{
                                      __html: service.shortDescription,
                                    }}
                                  ></div>
                                </div>

                                <div className="edit-delete d-flex">
                                  <span
                                    className="edit-link btn btn-secondary"
                                    style={{ margin: "0 3px" }}
                                    onClick={() =>
                                      navigate(`/dashboard/services/edit`, {
                                        state: { service },
                                      })
                                    }
                                  >
                                    <i
                                      className="fa-solid fa-pen-to-square"
                                      style={{ color: "#6b7280" }}
                                    ></i>
                                  </span>
                                  <span
                                    className="delete-link btn btn-danger"
                                    style={{ margin: "0 2px" }}
                                    onClick={() => {
                                      setServiceToDelete(
                                        service.platformServiceId
                                      );
                                      setShowDeletePopup(true);
                                    }}
                                  >
                                    <i
                                      className="fa-solid fa-trash-can"
                                      style={{ color: "#6b7280" }}
                                    ></i>
                                  </span>
                                </div>
                              </div>

                              <div
                                className="visibility-toggle"
                                style={{
                                  position: "relative",
                                  display: "inline-block",
                                  float: "right",
                                  marginRight: "4px",
                                  width: "100%",
                                  marginBottom: "5px",
                                  lineHeight: "0.1",
                                }}
                              >
                                <div
                                  className="eye-toggle-btn"
                                  onClick={() =>
                                    handleToggleIcon(service, index)
                                  }
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: "4px",
                                    float: "right",
                                  }}
                                >
                                  <i
                                    className={`fa-solid ${
                                      service.hideInProfileFlag
                                        ? "fa-eye-slash"
                                        : "fa-eye"
                                    }`}
                                    style={{ fontSize: "18px" }}
                                  ></i>
                                  {/* Tooltip inside button */}
                                  <span className="tooltip">
                                    {service.hideInProfileFlag
                                      ? "Hidden from profile"
                                      : "Visible in profile"}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="service-duration-price text-left">
                              {service.type === constant.TEXT_QUERY ? (
                                <span className="duration m-0">
                                  <i
                                    className="fa-regular fa-clock fa-sm"
                                    style={{ color: "#6b7280" }}
                                  ></i>
                                  Answer in 2 days
                                </span>
                              ) : (
                                <span className="duration m-0">
                                  <i
                                    className="fa-regular fa-clock fa-sm"
                                    style={{ color: "#6b7280" }}
                                  ></i>
                                  {service.callDuration} mins
                                </span>
                              )}
                              <span
                                className="price m-0"
                                style={{
                                  color:
                                    service.price === 0 || service.price === "0"
                                      ? "#4f46e5"
                                      : "#059669",
                                  background:
                                    service.price === 0 || service.price === "0"
                                      ? "#eef2ff"
                                      : " #e6fff3",
                                  border:
                                    service.price === 0 || service.price === "0"
                                      ? "1px solid #cfd7f0"
                                      : "1px solid #d0f3e4", //
                                  padding: "6px 12px",
                                  fontSize: "17px",
                                  fontWeight: "700",
                                  borderRadius: "6px",
                                  // border:"1px solid"
                                  /*background color previous #ecfdf5 */
                                }}
                              >
                                {getPriceUtility(currency, service.price)}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>

      {/* Delete popup */}
      {showDeletePopup && (
        <div
          className="delete-popup-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div className="delete-popup-box">
            <div className="d-flex align-items-center gap-3 mb-3">
              <i
                className="fa-solid fa-trash-can delete-icon fa-xl"
                style={{ color: "rgb(235 18 18)" }}
              ></i>
              <h4 className="modal-title ">Delete Service?</h4>
            </div>
            <p
              className="modal-message"
              style={{ color: "#555", marginBottom: "20px" }}
            >
              Are you sure you want to delete this service? This action cannot
              be undone and all data will be permanently removed.
            </p>
            <div className="d-flex gap-3 mt-3 justify-content-end">
              <button
                className="modal-btn-cancel"
                onClick={() => setShowDeletePopup(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger modal-btn-confirm"
                onClick={() => {
                  handleDelete(serviceToDelete);
                  setShowDeletePopup(false);
                  setServiceToDelete(null);
                }}
              >
                Delete Service
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Share modal */}

      {shareModalFor && (
        <div
          className="share-modal-overlay"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
          }}
        >
          <div className="share-modal-box" ref={shareRef}>
            <div className="d-flex align-items-center gap-3 mb-3">
              <i
                className="fa-solid fa-share-nodes share-icon fa-xl"
                style={{ color: "#ffffff" }}
              ></i>
              <h5 className="modal-title ">Share Service</h5>
            </div>
            <p
              className="modal-message"
              style={{ color: "#555", marginBottom: "20px" }}
            >
              Copy link to share this service with others. Anyone with the link
              will be able to view and book this service.
            </p>
            <h6 className="modal-message mb-0" style={{ marginTop: 8, color: "#555", }}>Copy link to share this service:</h6>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                marginTop: 2,
              }}
            >
              <input
                readOnly
                value={shareModalFor.link}
                style={{ flex: 1, padding: 8 }}
                className="share-input"
              />
              <button
                className="btn btn-outline-secondary copied"
                onClick={() => copyToClipboard(shareModalFor.link)}
              >
                {copied ? (
                  <>
                    <i
                      className="fa-solid fa-check"
                      style={{ color: "#ffffff", marginRight: 5 }}
                    ></i>
                    Copied
                  </>
                ) : (
                  <>
                    <i
                      className="fa-regular fa-copy"
                      style={{ color: "#ffffff", marginRight: 5 }}
                    ></i>
                    Copy
                  </>
                )}
              </button>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 12,
              }}
            >
              <button
                className="btn close-btn-confirm"
                onClick={() => setShareModalFor(null)}
              >
            <i className="fa-solid fa-xmark" style={{color: "rgb(67 68 68)"}}></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Services;
